import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Check, X, RefreshCw, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../ui/Button';
import vercelService from '../../services/vercelService';

interface VercelConnectProps {
  onStatusChange?: (connected: boolean) => void;
  compact?: boolean;
  configurationId?: string; // From Vercel dashboard
}

interface VercelStatus {
  connected: boolean;
  username?: string;
  email?: string;
  teamName?: string;
}

const VercelConnect: React.FC<VercelConnectProps> = ({ onStatusChange, compact = false, configurationId }) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<VercelStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const checkStatus = async () => {
    try {
      setIsLoading(true);
      const response = await vercelService.getStatus();
      if (response.success && response.data) {
        setStatus(response.data);
        onStatusChange?.(response.data.connected);
      }
    } catch (error) {
      console.error('Error checking Vercel status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleVercelCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const configId = urlParams.get('configurationId');
      const teamId = urlParams.get('teamId');

      // If we have a code from Vercel, exchange it for a token
      if (code) {
        setIsConnecting(true);
        try {
          const response = await vercelService.exchangeCode(code, configId || undefined, teamId || undefined);
          if (response.success) {
            toast.success(t('integrations.vercel.connected'));
            setStatus(response.data || { connected: true });
            onStatusChange?.(true);
          } else {
            toast.error(response.message || t('integrations.vercel.error'));
          }
        } catch (error: any) {
          console.error('Error exchanging Vercel code:', error);
          toast.error(error.response?.data?.message || error.message || t('integrations.vercel.error'));
        } finally {
          setIsConnecting(false);
          // Clean URL
          window.history.replaceState({}, '', window.location.pathname);
        }
        return;
      }

      // Check for other callback params
      if (urlParams.get('vercel') === 'connected') {
        toast.success(t('integrations.vercel.connected'));
        window.history.replaceState({}, '', window.location.pathname);
        checkStatus();
      } else if (urlParams.get('error')) {
        toast.error(urlParams.get('error') || t('integrations.vercel.error'));
        window.history.replaceState({}, '', window.location.pathname);
      }
    };

    checkStatus();
    handleVercelCallback();
  }, []);

  const handleConnect = async () => {
    try {
      setIsConnecting(true);
      await vercelService.connectVercel();
    } catch (error: any) {
      toast.error(error.message || t('integrations.vercel.connectError'));
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(t('integrations.vercel.confirmDisconnect'))) return;

    try {
      setIsDisconnecting(true);
      const response = await vercelService.disconnect();
      if (response.success) {
        setStatus({ connected: false });
        onStatusChange?.(false);
        toast.success(t('integrations.vercel.disconnected'));
      }
    } catch (error: any) {
      toast.error(error.message || t('integrations.vercel.disconnectError'));
    } finally {
      setIsDisconnecting(false);
    }
  };

  if (isLoading) {
    return (
      <div className={`flex items-center gap-2 ${compact ? '' : 'p-4 bg-gray-50 dark:bg-gray-800 rounded-xl'}`}>
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        <span className="text-gray-500 dark:text-gray-400">{t('common.loading')}</span>
      </div>
    );
  }

  if (compact) {
    // Compact version for PublishModal
    if (status?.connected) {
      return (
        <div className="flex items-center gap-2 text-sm">
          <Check className="w-4 h-4 text-green-500" />
          <span className="text-green-600 dark:text-green-400">
            Vercel: {status.username || status.email}
          </span>
        </div>
      );
    }

    return (
      <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center flex-shrink-0">
            <svg viewBox="0 0 76 65" className="w-5 h-5 fill-white">
              <path d="M37.5274 0L75.0548 65H0L37.5274 0Z" />
            </svg>
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-yellow-800 dark:text-yellow-200">
              {t('integrations.vercel.connectRequired')}
            </h4>
            <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
              {t('integrations.vercel.connectRequiredDesc')}
            </p>
            <Button
              size="sm"
              onClick={handleConnect}
              isLoading={isConnecting}
              className="mt-3"
              leftIcon={<ExternalLink className="w-4 h-4" />}
            >
              {t('integrations.vercel.connect')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Full version for Settings page
  return (
    <div className="p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center flex-shrink-0">
            <svg viewBox="0 0 76 65" className="w-6 h-6 fill-white">
              <path d="M37.5274 0L75.0548 65H0L37.5274 0Z" />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">Vercel</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('integrations.vercel.description')}
            </p>

            {status?.connected && (
              <div className="mt-3 flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
                  <Check className="w-4 h-4" />
                  {t('integrations.vercel.connectedAs')}
                </span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {status.username || status.email}
                </span>
                {status.teamName && (
                  <span className="text-gray-500 dark:text-gray-400">
                    ({status.teamName})
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {status?.connected ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={checkStatus}
                leftIcon={<RefreshCw className="w-4 h-4" />}
              >
                {t('common.refresh')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleDisconnect}
                isLoading={isDisconnecting}
                leftIcon={<X className="w-4 h-4" />}
              >
                {t('integrations.vercel.disconnect')}
              </Button>
            </>
          ) : (
            <Button
              onClick={handleConnect}
              isLoading={isConnecting}
              leftIcon={<ExternalLink className="w-4 h-4" />}
            >
              {t('integrations.vercel.connect')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VercelConnect;
